import { WowzaConfig } from '@/Common/wowza-config.model';
import { Injectable, EventEmitter } from '@angular/core';

export interface SecureData {
  applicationName: string;
  streamName: string;
  prefix: string;
  SharedKey: string;
  wowzatimeout: number;
  isIp: boolean;
  ip: string;
  hash?: string;
  startTime?: string;
  endTime?: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebrtcService {
  private wsConnection: WebSocket | null = null;
  private peerConnection: RTCPeerConnection | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private repeaterRetryCount = 0;

  private streamInfo: any;
  private userData: any;

  public streamError = new EventEmitter<string>();
  public codecInfo = new EventEmitter<string>();

  constructor() {}

  public async loadStream(videoElement: HTMLVideoElement, wowzaConfig: WowzaConfig): Promise<void> {
    this.stop();
    this.videoElement = videoElement;

    if (!this.videoElement) {
      this.streamError.emit("Video element not found.");
      return;
    }

    this.repeaterRetryCount = 0;

    const secureData: SecureData = {
      applicationName: wowzaConfig.applicationName,
      streamName: wowzaConfig.streamName,
      prefix: wowzaConfig.prefix,
      SharedKey: wowzaConfig.sharedKey,
      wowzatimeout: wowzaConfig.wowzaTimeout,
      isIp: wowzaConfig.isIp || false,
      ip: wowzaConfig.ip || ''
    };

    const processedStreamName = await this.parseStreamNameAndToken(
      secureData.streamName,
      secureData
    );

    this.streamInfo = {
      applicationName: wowzaConfig.applicationName,
      streamName: processedStreamName,
      sessionId: '[empty]'
    };

    this.userData = {
      iceServers: [
        {
          urls: ['stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302']
        }
      ],
      sdpSemantics: 'plan-b'
    };

    this.peerConnection = new RTCPeerConnection(this.userData);

    this.peerConnection.addEventListener("connectionstatechange", () => {
      const state = this.peerConnection!.connectionState;
      console.log("WebRTC connection state:", state);
      if (["disconnected", "failed", "closed"].includes(state)) {
        this.stop();
        this.streamError.emit("WebRTC connection dropped: " + state);
      }
    });

    this.peerConnection.addEventListener("iceconnectionstatechange", () => {
      const iceState = this.peerConnection!.iceConnectionState;
      console.log("ICE connection state:", iceState);
      if (iceState === "failed") {
        this.stop();
        this.streamError.emit("ICE connection failed");
      }
    });

    // Create WebSocket
    try {
      this.wsConnection = new WebSocket(wowzaConfig.wssUrl);
      this.wsConnection.binaryType = 'arraybuffer';
    } catch (e) {
      console.error("WebSocket failed to connect:", e);
      this.streamError.emit("WebSocket failed: " + e);
      return;
    }

    this.wsConnection.onopen = () => {
      this.peerConnection!.onicecandidate = this.gotIceCandidate;
      this.peerConnection!.ontrack = event => this.attachTrack(event);
      this.sendPlayGetOffer();
    };

    this.wsConnection.onmessage = evt => this.handleWsMessage(evt);
    this.wsConnection.onclose = () => console.warn("WebSocket closed");
    this.wsConnection.onerror = () => {
      console.error("WebSocket error");
      this.stop();
      this.streamError.emit("WebSocket error occurred");
    };
  }

  private attachTrack(event: RTCTrackEvent) {
    const stream = event.streams[0];

    if (!this.videoElement) return;

    this.videoElement.srcObject = stream;
    console.log("Stream attached.");

    // Track ended handling
    stream.getTracks().forEach(track => {
      track.onended = () => {
        if (this.videoElement?.srcObject === stream) {
          console.error("Media track ended");
          this.streamError.emit("Media track ended unexpectedly");
          this.stop();
        }
      };
    });

    setTimeout(() => {
      this.videoElement!.play()
        .then(() => console.log("Video playback started"))
        .catch(err => console.error("Video play() failed:", err));
    }, 500);
  }

  private detectCodecFromSDP(sdp: string) {
    // Example line: a=rtpmap:97 H264/90000


    console.log("sdp: " + sdp);

    const match = sdp.match(/a=rtpmap:\d+\s+([A-Za-z0-9\-]+)/);

    if (!match) {
      console.warn("Codec not found in SDP");
      return;
    }

    const codec = match[1]; // H264 / H265 / VP8 / VP9
    console.log("Detected video codec:", codec);

    this.codecInfo.emit(codec);
  }

  private handleWsMessage(evt: MessageEvent) {
    const msgJSON = JSON.parse(evt.data);
    const msgStatus = Number(msgJSON.status);
    const msgCommand = msgJSON.command;

    // Retry logic for Wowza stream not ready
    if (msgStatus === 514) {
      this.repeaterRetryCount++;
      console.warn(`Stream not found (514), retry ${this.repeaterRetryCount}`);

      if (this.repeaterRetryCount < 10) {
        setTimeout(() => this.sendPlayGetOffer(), 500);
      } else {
        this.stop();
        this.streamError.emit("Stream unavailable after retries.");
      }
      return;
    }

    if (msgStatus !== 200) {
      this.stop();
      this.streamError.emit(`Stream failed: status ${msgStatus}`);
      return;
    }

    this.handleSdpAndIce(msgJSON);

    if (msgCommand === 'sendResponse') {
      this.wsConnection?.close();
      this.wsConnection = null;
    }
  }

  private handleSdpAndIce(msgJSON: any) {
    if (msgJSON.streamInfo) {
      this.streamInfo.sessionId = msgJSON.streamInfo.sessionId;
    }

    const sdpData = msgJSON.sdp;

    if (sdpData && sdpData.sdp) {
      
      // *** Detect codec here ***
      this.detectCodecFromSDP(sdpData.sdp);

      this.peerConnection!.setRemoteDescription(new RTCSessionDescription(sdpData))
        .then(() => this.peerConnection!.createAnswer())
        .then(answer => {
          this.peerConnection!.setLocalDescription(answer).then(() => {
            this.wsConnection!.send(JSON.stringify({
              direction: "play",
              command: "sendResponse",
              streamInfo: this.streamInfo,
              sdp: answer,
              userData: this.userData
            }));
          });
        });
    }

    const iceCandidates = msgJSON.iceCandidates || [];
    for (const candidate of iceCandidates) {
      this.peerConnection!.addIceCandidate(new RTCIceCandidate(candidate));
    }
  }

  private gotIceCandidate = (event: RTCPeerConnectionIceEvent) => {
    if (event.candidate && this.wsConnection) {
      this.wsConnection.send(JSON.stringify({ ice: event.candidate }));
    }
  }

  private sendPlayGetOffer() {
    this.wsConnection?.send(JSON.stringify({
      direction: "play",
      command: "getOffer",
      streamInfo: this.streamInfo,
      userData: this.userData
    }));
  }

  // SECURITY TOKEN FUNCTIONS
  private async parseStreamNameAndToken(streamNameWithToken: string, secureData: SecureData): Promise<string> {
    const baseStreamName = streamNameWithToken.split('?')[0];
    const token = await this.getSecureToken(secureData);
    if (!token) return baseStreamName;

    secureData.hash = token.hash;
    const prefix = secureData.prefix || 'wowzatoken';

    let tokenQuery = `${prefix}hash=${token.hash}`;
    if (token.starttime) tokenQuery += `&${prefix}starttime=${token.starttime}`;
    if (token.endtime) tokenQuery += `&${prefix}endtime=${token.endtime}`;
    if (secureData.isIp && secureData.ip) tokenQuery += `&${prefix}ip=${secureData.ip}`;

    return `${baseStreamName}?${tokenQuery}`;
  }

  private async getSecureToken(secureData: SecureData): Promise<{ hash: string, starttime: string, endtime: string } | null> {
    const prefix = secureData.prefix || 'wowzatoken';
    let url = `${secureData.applicationName}/${secureData.streamName}?`;
    const queryParams = [secureData.SharedKey];

    if (secureData.isIp) queryParams.push(secureData.ip);

    if (secureData.wowzatimeout > 0) {
      const currTime = Math.floor(Date.now() / 1000);
      secureData.startTime = (currTime - 5).toString();
      secureData.endTime = (currTime + secureData.wowzatimeout).toString();
      queryParams.push(`${prefix}endtime=${secureData.endTime}`);
      queryParams.push(`${prefix}starttime=${secureData.startTime}`);
    }

    queryParams.sort();
    url += queryParams.join("&");

    const hash = await this.digestMessage(url);
    return {
      hash,
      starttime: secureData.startTime!,
      endtime: secureData.endTime!
    };
  }

  private async digestMessage(message: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    let hashBase64 = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
    return hashBase64.replace(/\//g, '_').replace(/\+/g, '-');
  }

  public stop() {
    try {
      if (this.videoElement) {
        this.videoElement.pause();
        this.videoElement.srcObject = null;
        this.videoElement.load();
      }

      if (this.peerConnection) {
        this.peerConnection.onicecandidate = null;
        this.peerConnection.ontrack = null;
        this.peerConnection.close();
      }

      if (this.wsConnection) {
        this.wsConnection.onmessage = null;
        this.wsConnection.onerror = null;
        this.wsConnection.onclose = null;
        this.wsConnection.close();
      }
    } catch (e) {
      console.warn("Stop error:", e);
    }

    this.peerConnection = null;
    this.wsConnection = null;
    this.videoElement = null;
    this.streamInfo = null;
    this.userData = null;
    this.repeaterRetryCount = 0;
  }
}
